<template>
  <article
    class="article-card"
    :class="template"
    :style="mainColorRubric"
  >
    <div class="article-card__content">
      <SecretElems
        v-if="isLoggedInWp"
        :article="article"
        class="article-card__secret"
      />

      <div
        class="article-card__header"
        :class="{ 'article-card__header--wrap': categories.length > 1 }"
      >
        <div
          v-if="!article.is_promo"
          class="article-card__categories"
        >
          <div
            v-for="category in categories"
            :key="category.id"
            class="article-card__category"
          >
            <BHLink
              class="article-card__category-link"
              :href="`/category/${category.slug}/`"
              colorless
              @click="sendAnalytics(category)"
            >
              <span v-html="category.title" />
            </BHLink>
          </div>
        </div>

        <span
          v-if="isShowAdsTag"
          class="article-card__category article-card__category--promo"
        >
          Реклама
        </span>

        <time
          class="article-card__date"
          :datetime="dateMoscow"
          >{{ date }}</time
        >
      </div>

      <div class="article-card__text-block">
        <h1
          class="article-card__title"
          v-html="article.title"
        />
        <p
          v-if="article.excerpt"
          class="article-card__description"
          v-html="article.excerpt"
        />
      </div>

      <div
        v-if="!isPromo"
        class="article-card__persons"
      >
        <ArticleCardPerson
          :person="article.author"
          class="article-card__person"
        />
      </div>

      <div
        v-if="article.image.sizes.length > 0 && sortedImages && sortedImages.length > 0"
        class="article-card__avatar"
        :class="{ 'article-card__avatar--promo': isPromo }"
      >
        <div class="article-card__image-wrapp">
          <AdaptivePictureArticleCard
            :picture="article.image"
            loading="eager"
          />
        </div>
      </div>

      <CardFooter
        class="article-card__footer"
        :hide-labels="hideLabelCardFooter"
        :hide-share="false"
        :hide-date="true"
        :hide-categories="true"
        :hide-reactions="false"
        :article="article"
        :is-show-comments-as-button="true"
      />
    </div>
  </article>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import ArticleCardPerson from '~/components/ArticleCard/ArticleCardPerson.vue';
  import CardFooter from '~/components/CardFooter.vue';
  import SecretElems from '~/components/SecretElems.vue';
  import AdaptivePictureArticleCard from '~/components/AdaptivePictureArticleCard.vue';
  import { getDateText } from '~/utils/date';
  import type { IFullArticle, TImageSize } from '~/types/common';
  import { filterImages } from '~/types/utils/images';
  import BHLink from '~/components/BHLink.vue';
  import getSlugFromUrl from '~/utils/getSlugFromUrl';
  import { useSingleArticleStore } from '~/store/singleArticle';
  import { useUserStore } from '~/features/user/store';
  import { mapState } from 'pinia';
  import getFullUrl from '~/utils/getFullUrl';
  import { convertBackendTimestampToMoscowString } from '@devhacker/shared/utils/date';

  export default defineNuxtComponent({
    name: 'ArticleCard',

    components: {
      ArticleCardPerson,
      CardFooter,
      BHLink,
      SecretElems,
      AdaptivePictureArticleCard,
    },

    inject: {
      // Категория в которой происходит событие
      // Для аналитики
      eventCategory: {
        default() {
          return () => '';
        },
      },
    },

    provide() {
      return {
        eventItemSharingList: () => 'вверху статьи',
        eventItemComments: () => 'верхняя иконка комментариев',
      };
    },

    props: {
      article: {
        type: Object as PropType<IFullArticle>,
        required: true,
      },

      hideLabelCardFooter: {
        type: Boolean as PropType<boolean>,
        default: false,
      },
    },

    data: () => ({
      coverWidth: [1496, 748, 576],
      coverHeight: [992, 496, 384],
    }),

    computed: {
      ...mapState(useUserStore, ['isLoggedInWp']),

      meta() {
        return useSingleArticleStore().meta;
      },

      isPromo(): boolean {
        return this.article?.is_promo;
      },

      isAdvertising(): boolean {
        return this.article?.is_advertising;
      },

      categories(): any {
        return this.article.categories.slice(0, 2);
      },

      date(): string {
        return this.getDateText(this.article.date);
      },

      dateMoscow(): string {
        return convertBackendTimestampToMoscowString(this.article.date);
      },

      mainColorRubric(): string {
        if (this.isAdvertising && !this.isCard) {
          return `
          --main-color-rubric: #EBEBEB;
        `;
        }

        if (!this.isPromo && !this.isCard) {
          return `
        --main-color-rubric: ${this.article.categories[0].color || '#FBF8F8'}
      `;
        }

        return '';
      },

      sortedImages(): TImageSize[][] {
        let sortedImages: TImageSize[] = [];
        if (this.images && this.images.length > 0) {
          sortedImages = [...this.images].sort((a, b) => a.width - b.width);
        }

        return [
          [sortedImages[0], sortedImages[1]],
          [sortedImages[1], sortedImages[2]],
        ];
      },

      images(): TImageSize[] | undefined {
        return this.filterImages(this.article.image.sizes, this.coverWidth, this.coverHeight);
      },

      isOneColumn(): boolean {
        return this.article?.template === 'one-column' || this.article?.template === 'promo';
      },

      isTwoColumns(): boolean {
        return (
          this.article?.template === 'two-column' ||
          this.article?.template === 'default' ||
          this.article?.template === ''
        );
      },

      isCard(): boolean {
        return this.article.template === 'cards';
      },

      isShowAdsTag(): boolean {
        return false;
      },

      currentSlug(): string {
        return getSlugFromUrl(this.$route.fullPath);
      },

      template(): Record<string, boolean> {
        return {
          'article-card--one-column': this.isOneColumn,
          'article-card--two-columns': this.isTwoColumns,
          'article-card--without-color': !this.categories[0].color,
          'article-card--cards': this.isCard,
        };
      },
    },

    methods: {
      getDateText,
      filterImages,

      getSrcSet(imageGroup: TImageSize[]) {
        return `${imageGroup[0].url} 1x, ${imageGroup[1].url} 2x`;
      },

      // Функция для отправки аналитики клика рубрики
      sendAnalytics(category: any) {
        this.$sendYandexMetrika({
          level1: 'Клик_Хлебные крошки',
          level4: getFullUrl(category.url),
          level5: category.title,
        });
      },
    },
  });
</script>

<style lang="scss" scoped>
  .article-card {
    background-image: linear-gradient(180deg, var(--main-color-rubric) 0%, #ffffff 72.26%);
    border-radius: 32px 32px 0 0;
    padding-top: $spacing-6;
    padding-left: $spacing-4;
    padding-right: $spacing-4;

    @include tablet {
      padding-left: $spacing-16;
      padding-right: $spacing-16;
      border-radius: 48px 48px 0 0;
    }

    @include desktop {
      padding-top: $spacing-9;
    }

    &__content {
      border-bottom: 1px solid $stroke-color--1;
      padding-bottom: $spacing-5;

      @include tablet {
        margin: 0 auto;
        max-width: 732px;
        padding-bottom: $spacing-4;
      }
    }

    &__secret {
      margin-bottom: $spacing-4;
      display: flex;
      align-items: center;

      @include tablet {
        margin-bottom: $spacing-5;
      }
    }

    &__secret-elem {
      &:not(:last-child) {
        margin-right: $spacing-2;
      }
    }

    &__header {
      margin-bottom: $spacing-3;
      font-family: $text-font--1;
      @include font(
        (
          'fs': 14px,
          'lh': 20px,
        )
      );

      display: flex;
      align-items: center;

      @include tablet {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        margin-bottom: $spacing-4;
      }

      &--wrap {
        flex-wrap: wrap;
        @include tablet {
          flex-wrap: nowrap;
        }

        .article-card__categories {
          margin-bottom: $spacing-3;
          margin-right: 0;

          @include tablet {
            margin-bottom: 0;
          }
        }

        .article-card__date {
          width: 100%;

          @include tablet {
            width: unset;
          }
        }
      }
    }

    &__categories {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      margin-right: $spacing-3;
    }

    &__category {
      border-radius: 16px;
      display: block;
      position: relative;
      overflow: hidden;
      background: var(--main-color-rubric);

      &:not(:last-child) {
        margin-right: $spacing-3;
      }

      &::before {
        position: absolute;
        width: 100%;
        height: 100%;
        background: #ffffff;
        opacity: 0.7;
        top: 0;
        left: 0;
        content: '';
        z-index: 0;
      }

      @include hover {
        &::before {
          opacity: 1;
        }
      }

      &--promo {
        padding: 6px $spacing-4;
        background: #fbf8f8;
        &::before {
          display: none;
        }
      }

      @include tablet {
        margin-bottom: 0;
        margin-right: $spacing-3;
      }
    }

    &__category-link {
      display: block;
      padding: 6px $spacing-4;
      z-index: 1;
      position: relative;
    }

    &__date {
      color: $text-color--2;
      @include tablet {
        width: unset;
      }
    }

    &__text-block {
      margin-bottom: $spacing-4;

      @include tablet {
        margin-bottom: $spacing-5;
      }

      font-family: $headline-font--1;
    }

    &__title {
      margin-bottom: $spacing-2;
      font-weight: 400;
      @include headline-normal-text;
      word-break: break-word;

      @include tablet {
        @include headline-big-text;
      }
    }

    &__description {
      word-break: break-word;
      @include mediumText;
      font-weight: 400;
      font-family: $text-font--1;
      @include tablet {
        @include extra-big-text;
      }

      * {
        font-weight: 400;
      }

      :deep() a {
        @include link-styles;
      }
    }

    &__persons {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin-bottom: $spacing-5;

      @include tablet {
        margin-bottom: $spacing-7;
      }
    }

    &__person {
      &:not(:last-child) {
        margin-right: $spacing-6;
      }
    }

    &__avatar {
      min-height: 192px;
      display: flex;
      margin-top: $spacing-5;
      margin-bottom: $spacing-4;
      position: relative;

      @include tablet {
        margin-top: $spacing-7;
      }

      &::before {
        content: '';
        display: block;
        padding-top: 66.666667%;
      }

      &--promo {
        margin-top: $spacing-4;
        @include tablet {
          margin-top: $spacing-6;
        }
      }
    }

    &__image-wrapp {
      border: 6px solid $border-color--1;
      border-radius: 32px;
      overflow: hidden;
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
    }

    &__image {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .article-card--two-columns {
    &.article-card--without-color {
      @include big-tablet {
        padding-top: 0;
      }
    }
    @include big-tablet {
      padding-left: $spacing-6;
      padding-right: $spacing-6;
    }

    @include desktop {
      padding-left: $spacing-19;
      padding-right: $spacing-19;
    }
  }

  .article-card--cards {
    .article-card__content {
      border-bottom: 0px;
      padding: 0px;
      @include big-tablet {
        max-width: none;
      }
    }
    @include tablet {
      padding-left: 56px;
      padding-right: 56px;
    }
    @include big-tablet {
      padding-left: $spacing-6;
      padding-right: $spacing-6;
    }

    @include desktop {
      padding-left: $spacing-17;
      padding-right: $spacing-17;
    }
  }

  .article-card--one-column .article-card {
    &__content {
      @include tablet {
        max-width: 732px;
        margin: 0 auto;
      }
    }
  }

  :deep() {
    .card-footer {
      &__button {
        @include hover {
          color: #000;
          .card-footer {
            &__icon {
              color: #000;
            }
          }
        }
      }
      .card-footer__categories--category {
        @include hover {
          color: #000;
        }
      }
    }
  }
</style>
